import { useEffect, useState } from "react";
import type { Lang } from "../lib/i18n";
import { pick } from "../lib/i18n";
import { getQAForPath, subscribeQA, sanitizeHtml } from "../data/qa";

interface FaqScreenProps {
  lang: Lang;
  careerPath: string;
  onNext: () => void;
  onBack: () => void;
}

/** Step 5 — department FAQ accordion, filtered by the chosen career path. */
export function FaqScreen({ lang, careerPath, onNext, onBack }: FaqScreenProps) {
  const t = (ar: string, en: string) => pick(lang, ar, en);
  const [list, setList] = useState(() => getQAForPath(careerPath));
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setList(getQAForPath(careerPath));
    return subscribeQA(() => setList(getQAForPath(careerPath)));
  }, [careerPath]);

  const toggle = (id: string) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const expandAll = () => setOpenIds(new Set(list.map((q) => q.id)));
  const collapseAll = () => setOpenIds(new Set());

  return (
    <div className="kd-step-container kd-qa-step" dir={lang === "ar" ? "rtl" : "ltr"}>
      <div className="glass-panel">
        <div className="kd-step-header">
          <h1>{t("أسئلة القسم", "Department Q&A")}</h1>
          <p>
            {t(
              "تعرّف على أهم المعلومات والسياسات الخاصة بقسمك وبشركة كادن. اضغط على أي سؤال لعرض إجابته.",
              "Learn the key information and policies for your department and for Kaden Investment. Click any question to view its answer.",
            )}
          </p>
          <small className="kd-suggested-note">
            {t(
              "هذه الخطوة للاطلاع الحر وليست إلزامية، يمكنك المتابعة إلى الخطوة التالية في أي وقت.",
              "This step is for optional reference, you may proceed to the next step at any time.",
            )}
          </small>
        </div>

        {list.length === 0 ? (
          <div className="kd-qa-empty">
            <div className="kd-qa-empty-icon">💬</div>
            <h3>{t("لم تُضف أسئلة لهذا القسم بعد", "No questions added for this department yet")}</h3>
            <p>{t("يمكنك المتابعة إلى الخطوة التالية. سيقوم الأدمن بإضافة أسئلة قسمك قريباً.", "You can proceed to the next step. The admin will add your department questions soon.")}</p>
          </div>
        ) : (
          <>
            <div className="kd-qa-toolbar">
              <span className="kd-qa-count">{t(`عدد الأسئلة: ${list.length}`, `${list.length} question${list.length === 1 ? "" : "s"}`)}</span>
              <div className="kd-qa-toolbar-btns">
                <button className="kd-qa-toolbar-btn" onClick={expandAll}>{t("فتح الكل", "Expand all")}</button>
                <button className="kd-qa-toolbar-btn" onClick={collapseAll}>{t("طيّ الكل", "Collapse all")}</button>
              </div>
            </div>
            <div className="kd-qa-list">
              {list.map((item, i) => {
                const isOpen = openIds.has(item.id);
                const question = lang === "ar" ? item.questionAr : item.questionEn;
                const answer = lang === "ar" ? item.answerHtmlAr : item.answerHtmlEn;
                return (
                  <div className={`kd-qa-item${isOpen ? " is-open" : ""}`} key={item.id}>
                    <button className="kd-qa-question" onClick={() => toggle(item.id)} aria-expanded={isOpen} aria-controls={`qa-answer-${item.id}`}>
                      <span className="kd-qa-num">{String(i + 1).padStart(2, "0")}</span>
                      <span className="kd-qa-q-text">{question}</span>
                      <span className="kd-qa-chevron" aria-hidden>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </span>
                    </button>
                    {isOpen && <div id={`qa-answer-${item.id}`} className="kd-qa-answer" dangerouslySetInnerHTML={{ __html: sanitizeHtml(answer) }} />}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="kd-side-arrows-lg">
        <button className="kd-side-arrow-lg kd-side-arrow-prev" onClick={onBack}>
          <span className="kd-side-arrow-label">{t("السابق", "Previous")}</span>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points={lang === "ar" ? "9 18 15 12 9 6" : "15 18 9 12 15 6"} />
          </svg>
        </button>
        <button className="kd-side-arrow-lg kd-side-arrow-next" onClick={onNext}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points={lang === "ar" ? "15 18 9 12 15 6" : "9 18 15 12 9 6"} />
          </svg>
          <span className="kd-side-arrow-label">{t("التالي", "Next")}</span>
        </button>
      </div>
    </div>
  );
}
